import cx from 'classnames';
import { uniq } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import {
  type Control,
  type UseFormResetField,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { ControlledFormTableIconRadio } from '@components/formInputs/ControlledFormTableIconRadio';
import { IconButton } from '@components/iconButton/IconButton';
import { TURN_TIME_OPTIONS } from '@shared/types/turnTimes';
import typography from '~styles/typography.scss';
import { useTableShapes } from 'restaurantAdmin/hooks/useTableShapes';
import { useRestaurant } from '../../../context/useRestaurant';
import type { ListingFormData } from '../types';
import styles from './AdvancedOptionsFieldSet.scss';
import { FloorPlanIcons } from '@components/icon/FloorPlanIcons';

interface AdvancedOptionsFieldSetProps {
  control: Control<ListingFormData>;
  isNewListing: boolean;
  setValue: UseFormSetValue<ListingFormData>;
  resetField: UseFormResetField<ListingFormData>;
}
export const AdvancedOptionsFieldSet = ({
  control,
  isNewListing,
  setValue,
  resetField,
}: AdvancedOptionsFieldSetProps) => {
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [shouldAutoPopulate, setShouldAutoPopulate] = useState(isNewListing);

  const floorPlanId = useWatch({
    control,
    name: 'floorPlanId',
  });

  const previousFloorPlanIdRef = useRef<string>();

  const highlightedTables = useWatch({
    control,
    name: 'highlightedTables',
  });

  const sortTableNames = () => {
    const isNumeric = highlightedTables.every(
      (table) => !Number.isNaN(Number(table.name)),
    );
    return isNumeric
      ? highlightedTables
          .map((table) => Number(table.name))
          .sort((a, b) => a - b)
          .join(',')
      : highlightedTables
          .map((table) => table.name)
          .sort()
          .join(',');
  };

  const getDefaultMetaData = () => ({
    name: sortTableNames(),
    minimumGuests: String(
      Math.min(...highlightedTables.map((table) => table.minimumGuests)),
    ),
    maximumGuests: String(
      Math.max(...highlightedTables.map((table) => table.maximumGuests)),
    ),
    turnTime: Math.max(...highlightedTables.map((table) => table.turnTime)),
    iconName: highlightedTables[0].iconName,
  });

  useEffect(() => {
    if (shouldAutoPopulate) {
      if (!highlightedTables.length) {
        resetField('name');
        resetField('minimumGuests');
        resetField('maximumGuests');
        resetField('turnTime');
        resetField('iconName');
      } else {
        const defaults = getDefaultMetaData();
        setValue('name', defaults.name);
        setValue('minimumGuests', defaults.minimumGuests);
        setValue('maximumGuests', defaults.maximumGuests);
        setValue('turnTime', defaults.turnTime);
        setValue('iconName', defaults.iconName);
      }
    }
  }, [highlightedTables]);

  useEffect(() => {
    // `floorPlanId` changes as part of form initialization. This ensures that
    // the highlightedTables are only reset when the floorPlanId changes from a
    // one _defined_ value to another.
    if (previousFloorPlanIdRef.current) {
      setValue('highlightedTables', []);
      setIsAdvancedOptionsOpen(false);
      setShouldAutoPopulate(true);
    }
    previousFloorPlanIdRef.current = floorPlanId;
  }, [floorPlanId]);

  const handleOnOpenAdvancedOptions = () => {
    setIsAdvancedOptionsOpen(true);
    setShouldAutoPopulate(false);
  };

  const renderContent = () => {
    if (isAdvancedOptionsOpen) {
      return <EditableAdvancedOptions control={control} />;
    }

    if (highlightedTables.length) {
      return <DefaultAdvancedOptions control={control} />;
    }

    return <EmptyAdvancedOptions />;
  };

  const isEditable = !isAdvancedOptionsOpen && !!highlightedTables.length;

  return (
    <fieldset className={styles.fieldset}>
      <h3 className={typography.t3}>advanced options</h3>
      {isEditable && (
        <IconButton
          className={cx(typography.t1, styles.editButton)}
          ariaLabel="Edit"
          iconName="pencil"
          showLabel
          onClick={handleOnOpenAdvancedOptions}
        />
      )}
      {renderContent()}
    </fieldset>
  );
};

const EmptyAdvancedOptions = () => (
  <p className={typography.c2_20}>
    Select at least one table to highlight in this listing to auto-fill the
    Advanced Options.
  </p>
);

interface DefaultAdvancedOptionsProps {
  control: Control<ListingFormData>;
}
const DefaultAdvancedOptions = ({ control }: DefaultAdvancedOptionsProps) => {
  const [iconName, name, minimumGuests, maximumGuests, turnTime] = useWatch({
    control,
    name: ['iconName', 'name', 'minimumGuests', 'maximumGuests', 'turnTime'],
  });

  return (
    <dl>
      <div>
        <div className={styles.displayIcon}>
          <FloorPlanIcons name={iconName} ariaLabel={iconName} />
        </div>
        <div>
          <dt>Staff Name</dt>
          <dd>{name}</dd>
        </div>
      </div>
      <div>
        <dt>Min - Max Guests</dt>
        <dd>{`${minimumGuests}-${maximumGuests}`}</dd>
      </div>
      <div>
        <dt>Turn time</dt>
        <dd>{`${turnTime} mins`}</dd>
      </div>
    </dl>
  );
};

interface EditableAdvancedOptionsProps {
  control: Control<ListingFormData>;
}
const EditableAdvancedOptions = ({ control }: EditableAdvancedOptionsProps) => {
  const { maxReservationGuests } = useRestaurant();
  const { humanReadableIconNames } = useTableShapes([]);

  const [highlightedTables, minimumGuests, maximumGuests] = useWatch({
    control,
    name: ['highlightedTables', 'minimumGuests', 'maximumGuests'],
  });

  const maxGuestRules = {
    max: {
      message: `The Max Guest count cannot exceed the configured setting of ${maxReservationGuests} guests`,
      value: maxReservationGuests,
    },
    min: {
      message: 'Max Guest must be greater than or equal to Min Guest',
      value: minimumGuests,
    },
    required: true,
  };

  return (
    <>
      <ControlledFormTableIconRadio
        control={control}
        humanReadableIconNames={humanReadableIconNames}
        label="Display Icon"
        name="iconName"
        tableIconNames={uniq(highlightedTables.map((table) => table.iconName))}
      />
      <ControlledFormInput
        control={control}
        type="text"
        label="Staff Name"
        name="name"
        placeholder="2 Top (Outside)"
        tooltipText="The internal name for the table. Seen only by Admins."
        rules={{
          required: true,
        }}
      />
      <ControlledFormInput
        control={control}
        type="number"
        label="Min Guest"
        name="minimumGuests"
        rules={{
          max: {
            message: `Min Guest must be less than or equal to Max Guest`,
            value: maximumGuests,
          },
          min: {
            message: `A reservation must seat at least 1 guest`,
            value: 1,
          },
          required: true,
        }}
      />
      <ControlledFormInput
        control={control}
        type="number"
        label="Max Guest"
        name="maximumGuests"
        rules={maxGuestRules}
      />
      <ControlledFormSelect
        control={control}
        label="Turn Time"
        name="turnTime"
        options={TURN_TIME_OPTIONS}
        tooltipText="An estimate of how long guests will usually sit at the table, plus the time it takes to clear and set the table for the next guest"
        rules={{
          required: true,
        }}
      />
    </>
  );
};
